import { useState, useEffect } from "react";

interface SidebarProps {
  aktifMenu: string;
  onMenuChange: (menu: string) => void;
  mobileAcik: boolean;
  onMobileKapat: () => void;
}

const menuItems = [
  { id: "genel-bakis", label: "Genel Bakış", ikon: "ri-dashboard-3-line" },
  { id: "ziyaretci-akisi", label: "Ziyaretçi Akışı", ikon: "ri-flow-chart" },
  { id: "kampanya-analizi", label: "Kampanya Analizi", ikon: "ri-bar-chart-2-line" },
  { id: "basvurular", label: "Başvurular", ikon: "ri-file-list-3-line" },
  { id: "whatsapp-hareketleri", label: "WhatsApp Hareketleri", ikon: "ri-whatsapp-line" },
  { id: "popup-performansi", label: "Teklif / Popup Performansı", ikon: "ri-notification-3-line" },
  { id: "reklam-olustur", label: "Reklam Oluştur", ikon: "ri-rocket-2-line" },
  { id: "ayarlar", label: "Ayarlar", ikon: "ri-settings-3-line" },
];

export default function Sidebar({ aktifMenu, onMenuChange, mobileAcik, onMobileKapat }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  // Ekran boyutu değişince collapsed sıfırla
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setCollapsed(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMenuClick = (id: string) => {
    onMenuChange(id);
    // Mobilde menü seçince kapat
    if (window.innerWidth < 1024) {
      onMobileKapat();
    }
  };

  return (
    <>
      {/* Mobil overlay */}
      {mobileAcik && (
        <div
          className="fixed inset-0 z-30 lg:hidden"
          style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          onClick={onMobileKapat}
        />
      )}

      <aside
        className={`fixed left-0 top-0 h-full flex flex-col transition-all duration-300 z-40
          ${mobileAcik ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
          ${collapsed ? "lg:w-16" : "lg:w-64"}
          w-72
        `}
        style={{
          backgroundColor: "#0a0c12",
          borderRight: "1px solid #1e2130",
        }}
      >
        {/* Logo */}
        <div
          className="flex items-center gap-3 px-4 py-5"
          style={{ borderBottom: "1px solid #1e2130" }}
        >
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #7c3aed, #4f46e5)" }}
          >
            <i className="ri-rocket-2-line text-white text-base"></i>
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <p className="text-sm font-bold whitespace-nowrap leading-tight" style={{ color: "#e8eaf0" }}>Smart Pro</p>
              <p className="text-xs whitespace-nowrap" style={{ color: "#4a5068" }}>Platform</p>
            </div>
          )}
          {/* Desktop collapse butonu */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto transition-colors cursor-pointer flex-shrink-0 hidden lg:flex items-center justify-center"
            style={{ color: "#4a5068" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#8b8fa8")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#4a5068")}
          >
            <i className={`${collapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line"} text-lg`}></i>
          </button>
          {/* Mobil kapat butonu */}
          <button
            onClick={onMobileKapat}
            className="ml-auto transition-colors cursor-pointer flex-shrink-0 flex items-center justify-center lg:hidden"
            style={{ color: "#4a5068" }}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = aktifMenu === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-200 cursor-pointer relative group"
                style={{
                  backgroundColor: isActive ? "rgba(124,58,237,0.15)" : "transparent",
                  color: isActive ? "#a78bfa" : "#5a5f7a",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)";
                    e.currentTarget.style.color = "#8b8fa8";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#5a5f7a";
                  }
                }}
              >
                {isActive && (
                  <span
                    className="absolute left-0 top-0 bottom-0 w-0.5 rounded-r-full"
                    style={{ backgroundColor: "#7c3aed" }}
                  />
                )}
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.ikon} text-lg`}></i>
                </div>
                {(!collapsed || window.innerWidth < 1024) && (
                  <span className="text-sm font-medium">{item.label}</span>
                )}
                {collapsed && window.innerWidth >= 1024 && (
                  <div
                    className="absolute left-full ml-2 px-2 py-1 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50"
                    style={{ backgroundColor: "#1e2130" }}
                  >
                    {item.label}
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        {/* User Profile */}
        <div
          className={`p-4 ${collapsed ? "lg:flex lg:justify-center" : ""}`}
          style={{ borderTop: "1px solid #1e2130" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #7c3aed, #4f46e5)" }}
            >
              <span className="text-white text-sm font-bold">AY</span>
            </div>
            {!collapsed && (
              <div className="overflow-hidden">
                <p className="text-sm font-semibold whitespace-nowrap" style={{ color: "#c8cad8" }}>Admin Yönetici</p>
                <p className="text-xs whitespace-nowrap" style={{ color: "#4a5068" }}>Süper Admin</p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
